"use client";

import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const GradientColors = ({ colors = {
    start: "#18CCFC",
    middle: "#6344F5",
    end: "#AE48FF"
} }) => {
    return (
        <>
            <stop offset="0%" stopColor={colors.start} stopOpacity="0" />
            <stop offset="20%" stopColor={colors.start} stopOpacity="1" />
            <stop offset="50%" stopColor={colors.middle} stopOpacity="1" />
            <stop offset="100%" stopColor={colors.end} stopOpacity="0" />
        </>
    );
};

export const PulseBeamBorder = ({ children, className }) => {
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setDimensions({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                });
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const { width, height } = dimensions;
    const path = `M2 2 H${width - 2} V${height - 2} H2 Z`;
    const totalLength = (width * 2 + height * 2);

    return (
        <div
            ref={containerRef}
            className={cn("relative pointer-events-auto", className)}
        >
            {/* SVG Container */}
            <div className="absolute inset-0 pointer-events-none rounded-inherit overflow-hidden z-0">
                {width > 0 && height > 0 && (
                    <svg
                        width="100%"
                        height="100%"
                        viewBox={`0 0 ${width} ${height}`}
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="absolute inset-0"
                    >
                        {/* Base Border */}
                        <path
                            d={path}
                            stroke="var(--violet-200)"
                            strokeWidth="1"
                            className="dark:stroke-violet-800"
                        />

                        {/* Animated Beam */}
                        {/* Note: Calculated strokeDasharray/offset for smooth loop based on perimeter */}
                        <motion.path
                            d={path}
                            stroke="url(#beam-gradient-border)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            initial={{ strokeDasharray: `40 ${totalLength}`, strokeDashoffset: 0 }}
                            animate={{ strokeDashoffset: [-totalLength, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                        />

                        <defs>
                            <linearGradient id="beam-gradient-border" x1="0%" y1="0%" x2="100%" y2="0%">
                                <GradientColors />
                            </linearGradient>
                        </defs>
                    </svg>
                )}
            </div>

            {/* Content */}
            <div className="relative z-10 h-full w-full">
                {children}
            </div>
        </div>
    );
};
